import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Empty, Tabs, Progress, Input, Segmented, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import operatorApiFetch from '@services/operator/apiService';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import MerchantSelect from '@components/operator/MerchantSelect';
import PageHeader from '@shared/PageHeader';
import AffinityGraph from './AffinityGraph';

const strengthColor = (strength) => {
  if (strength >= 70) return '#52c41a';
  if (strength >= 40) return '#1890ff';
  return '#8c8c8c';
};

const RelatedCell = ({ related, scoreLabel }) => {
  if (!related) {
    return <div style={{ color: '#ccc', textAlign: 'center' }}>—</div>;
  }

  return (
    <div>
      <div style={{ fontWeight: 500 }}>{related.name}</div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{related.sku}</div>
      <Progress
        percent={related.strength}
        size="small"
        strokeColor={strengthColor(related.strength)}
        format={(pct) => `${pct}%`}
      />
      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
        {scoreLabel}: <strong>{related.score}</strong>
      </div>
    </div>
  );
};

function SlottingAnalysisContent() {
  const { t } = useTranslation();
  const { merchantId } = useOperatorData();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [sortMetric, setSortMetric] = useState('score'); // 'score' | 'strength'

  useEffect(() => {
    if (merchantId) fetchData();
  }, [merchantId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 100,
        merchant_ids: merchantId,
      });

      const response = await operatorApiFetch(`reports/product_affinities?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data.affinities);
      }
    } catch (error) {
      console.error('Fetch affinity report failed', error);
    } finally {
      setLoading(false);
    }
  };

  // Group raw pairs by primary product, keep top 3 related sorted by the chosen metric.
  // When user picks "strength", the rank slots also re-order so rank1 = strongest affinity.
  const groupedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const groups = new Map();
    data.forEach(item => {
      const key = item.product_sku;
      if (!groups.has(key)) {
        groups.set(key, {
          product_name: item.product_name,
          product_sku: item.product_sku,
          related: [],
        });
      }
      groups.get(key).related.push({
        name: item.related_product_name,
        sku: item.related_product_sku,
        score: item.score,
        strength: item.strength,
      });
    });

    return Array.from(groups.values())
      .map(g => {
        const sorted = [...g.related].sort((a, b) => b[sortMetric] - a[sortMetric]);
        return {
          key: g.product_sku,
          product_name: g.product_name,
          product_sku: g.product_sku,
          rank1: sorted[0] || null,
          rank2: sorted[1] || null,
          rank3: sorted[2] || null,
          topMetric: sorted[0]?.[sortMetric] ?? 0,
        };
      })
      .sort((a, b) => b.topMetric - a.topMetric);
  }, [data, sortMetric]);

  // Match against primary product + any of the top-3 related (name or SKU, case-insensitive)
  const filteredData = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return groupedData;

    const hit = (r) => r && (
      (r.name || '').toLowerCase().includes(q) ||
      (r.sku || '').toLowerCase().includes(q)
    );

    return groupedData.filter(row =>
      (row.product_name || '').toLowerCase().includes(q) ||
      (row.product_sku || '').toLowerCase().includes(q) ||
      hit(row.rank1) || hit(row.rank2) || hit(row.rank3)
    );
  }, [groupedData, searchText]);

  const scoreLabel = t('operator.reports.slotting_analysis.score');

  const rankMetric = (rank) => rank?.[sortMetric] ?? -1;

  const columns = [
    {
      title: t('operator.reports.slotting_analysis.primary_product'),
      dataIndex: 'product_name',
      key: 'primary',
      width: 200,
      sorter: (a, b) => a.topMetric - b.topMetric,
      defaultSortOrder: 'descend',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.product_sku}</div>
        </div>
      ),
    },
    {
      title: t('operator.reports.slotting_analysis.most_related'),
      dataIndex: 'rank1',
      key: 'rank1',
      sorter: (a, b) => rankMetric(a.rank1) - rankMetric(b.rank1),
      render: (r) => <RelatedCell related={r} scoreLabel={scoreLabel} />,
    },
    {
      title: t('operator.reports.slotting_analysis.second_related'),
      dataIndex: 'rank2',
      key: 'rank2',
      sorter: (a, b) => rankMetric(a.rank2) - rankMetric(b.rank2),
      render: (r) => <RelatedCell related={r} scoreLabel={scoreLabel} />,
    },
    {
      title: t('operator.reports.slotting_analysis.third_related'),
      dataIndex: 'rank3',
      key: 'rank3',
      sorter: (a, b) => rankMetric(a.rank3) - rankMetric(b.rank3),
      render: (r) => <RelatedCell related={r} scoreLabel={scoreLabel} />,
    },
  ];

  const items = [
    {
      key: 'graph',
      label: t('operator.reports.slotting_analysis.view_graph', { defaultValue: 'Network Graph' }),
      children: <AffinityGraph data={data} />,
    },
    {
      key: 'table',
      label: t('operator.reports.slotting_analysis.view_table', { defaultValue: 'Data Table' }),
      children: (
        <>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder={t('operator.reports.slotting_analysis.search_placeholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 320 }}
            />
            <Segmented
              value={sortMetric}
              onChange={setSortMetric}
              options={[
                { label: t('operator.reports.slotting_analysis.score'), value: 'score' },
                { label: t('operator.reports.slotting_analysis.strength'), value: 'strength' },
              ]}
            />
          </Space>
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey="key"
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description={t('common.no_data')} /> }}
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <PageHeader items={['reports', 'reports.slotting_analysis']} />
      <Card bordered={false}>
        <Tabs defaultActiveKey="graph" items={items} />
      </Card>
    </div>
  );
}

export default function SlottingAnalysis() {
  return (
    <MerchantSelect>
      <SlottingAnalysisContent />
    </MerchantSelect>
  );
}
